import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import logo from "../Navbar/logo3.jpg"; // Adjust the path to your logo file

const CourseRegistration = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Get user data from store
  const { userInfo } = useSelector((state) => state?.users?.userAuth);
  const user = userInfo?.userFound || {};
  
  const [formData, setFormData] = useState({
    fullname: '',
    email: '',
    courseDate: '',
    courseType: '',
    additionalInfo: ''
  });
  
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errors, setErrors] = useState({});

  // Update form when user data is loaded - only once
  useEffect(() => {
    if (user?.fullname || user?.email) {
      setFormData(prev => ({
        ...prev,
        fullname: user.fullname || prev.fullname,
        email: user.email || prev.email
      }));
    }
  }, [user?.fullname, user?.email]); // Only depend on specific user properties

  // Available courses
  const courses = [
    { id: 'beginner', name: t('courses.beginner.name'), duration: '4 hours', price: 'Free' },
    { id: 'intermediate', name: t('courses.intermediate.name'), duration: '6 hours', price: '$49' },
    { id: 'advanced', name: t('courses.advanced.name'), duration: '8 hours', price: '$79' },
    { id: 'masterclass', name: t('courses.masterclass.name'), duration: '12 hours', price: '$129' }
  ];

  // Available dates (next 30 days)
  const getAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 1; i <= 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      // Skip weekends if needed
      if (date.getDay() !== 0 && date.getDay() !== 6) {
        dates.push(date);
      }
    }
    return dates;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.fullname.trim()) {
      newErrors.fullname = t('courses.errors.fullnameRequired');
    }
    if (!formData.email.trim()) {
      newErrors.email = t('courses.errors.emailRequired');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('courses.errors.emailInvalid');
    }
    if (!formData.courseDate) {
      newErrors.courseDate = t('courses.errors.dateRequired');
    }
    if (!formData.courseType) {
      newErrors.courseType = t('courses.errors.courseRequired');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      // Here you would dispatch an action to save the registration
      console.log('Form submitted:', formData);
      setIsSubmitted(true);
      
      // Reset after 3 seconds or redirect
      setTimeout(() => {
        setIsSubmitted(false);
        // Optionally redirect to confirmation page
        // navigate('/registration-confirmation');
      }, 3000);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="flex justify-center mb-6">
            <div className="relative h-24 w-24 overflow-hidden rounded-full border-2 border-amber-500 bg-gray-900 transition-transform hover:scale-105">
              <img
                className="h-full w-full object-cover"
                src={logo}
                alt="Mexa Beer Logo"
              />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {t('courses.success.title')}
          </h2>
          <p className="text-gray-600 mb-6">
            {t('courses.success.message')}
          </p>
          <p className="text-sm text-gray-500">
            {t('courses.success.confirmationEmail')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 to-white py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto">
        {/* Header with Logo */}
        <div className="text-center mb-12">
          <div className="flex justify-center mb-4">
            <div className="relative h-20 w-20 overflow-hidden rounded-full border-2 border-amber-500 bg-gray-900 transition-transform hover:scale-105">
              <img
                className="h-full w-full object-cover"
                src={logo}
                alt="Mexa Beer Logo"
              />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            {t('courses.title')}
          </h1>
          <p className="text-lg text-gray-600">
            {t('courses.subtitle')}
          </p>
        </div>

        {/* Form */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {/* Full Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {t('courses.fullname')}
                </div>
              </label>
              <input
                type="text"
                name="fullname"
                value={formData.fullname}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
                  errors.fullname ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('courses.fullnamePlaceholder')}
              />
              {errors.fullname && (
                <p className="mt-1 text-sm text-red-600">{errors.fullname}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  {t('courses.email')}
                </div>
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
                  errors.email ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={t('courses.emailPlaceholder')}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Course Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                  {t('courses.selectCourse')}
                </div>
              </label>
              <select
                name="courseType"
                value={formData.courseType}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
                  errors.courseType ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('courses.selectCoursePlaceholder')}</option>
                {courses.map(course => (
                  <option key={course.id} value={course.id}>
                    {course.name} - {course.duration} ({course.price})
                  </option>
                ))}
              </select>
              {errors.courseType && (
                <p className="mt-1 text-sm text-red-600">{errors.courseType}</p>
              )}
            </div>

            {/* Course Date */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {t('courses.selectDate')}
                </div>
              </label>
              <select
                name="courseDate"
                value={formData.courseDate}
                onChange={handleChange}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors ${
                  errors.courseDate ? 'border-red-500' : 'border-gray-300'
                }`}
              >
                <option value="">{t('courses.selectDatePlaceholder')}</option>
                {getAvailableDates().map(date => (
                  <option key={date.toISOString()} value={date.toISOString()}>
                    {date.toLocaleDateString(undefined, { 
                      weekday: 'long', 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </option>
                ))}
              </select>
              {errors.courseDate && (
                <p className="mt-1 text-sm text-red-600">{errors.courseDate}</p>
              )}
            </div>

            {/* Additional Info */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('courses.additionalInfo')}
              </label>
              <textarea
                name="additionalInfo"
                value={formData.additionalInfo}
                onChange={handleChange}
                rows="4"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-amber-500 transition-colors"
                placeholder={t('courses.additionalInfoPlaceholder')}
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-amber-600 hover:bg-amber-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-200 flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {t('courses.registerButton')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CourseRegistration;